import { observer } from 'mobx-react';
import styled from 'styled-components';

// Components
import { Alert, Button, Container, CustomInlineForm, Modal, notification } from 'top-component-library';
import TextInput from 'top-component-library/Fields/TextInput';

// constants
import breakpoints from 'constants/breakpoints';
import { header } from 'constants/style';

// hooks
import { useStores } from 'hooks/use-stores';
import { useState } from 'react';

function ReaderPairingModal() {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { locationStore, stripeTerminalStore } = useStores();
  const [waitingForStripe, setWaitingForStripe] = useState(false);

  const pairReader = async (reader_registration_code) => {
    try {
      setWaitingForStripe(true);
      const res = await stripeTerminalStore.pairStripeInternetReader(locationStore.id, reader_registration_code);
      notification.success({ message: res.message });
    } catch (error) {
      notification.error({ message: error.message });
    } finally {
      setWaitingForStripe(false);
    }
  };

  return (
    <ReaderPairingModal.Modal
      backdropClassName="modal-dark-backdrop"
      centered
      closable={false}
      footer={null}
      onCancel={null}
      visible
      width={1000}
    >
      <ReaderPairingModal.Container>
        <ReaderPairingModal.Header>
          <ReaderPairingModal.Title>Card Reader Pairing</ReaderPairingModal.Title>
          <ReaderPairingModal.Description>
            Oops! This Kiosk has no card reader attached to it. To pair a reader, please follow the steps below.
          </ReaderPairingModal.Description>
        </ReaderPairingModal.Header>
        <ReaderPairingModal.Content>
          <ReaderPairingModal.InstructionsList>
            {ReaderPairingSteps.map((pairingStep, index) => (
              <ReaderPairingModal.InstructionsListItem key={index}>
                {pairingStep}
              </ReaderPairingModal.InstructionsListItem>
            ))}
          </ReaderPairingModal.InstructionsList>
        </ReaderPairingModal.Content>
        <ReaderPairingModal.FormContent>
          <CustomInlineForm
            label="Enter Pairing Code"
            buttonText="Pair Reader"
            buttonTestId="pair-reader-button"
            formInput={<TextInput allowClear name="reader_registration_code" placeholder="reader-pairing-code" />}
            onSubmit={pairReader}
            loading={waitingForStripe}
          />
        </ReaderPairingModal.FormContent>
        {stripeTerminalStore.androidInitializationError && (
          <ReaderPairingModal.AdvancedOptions.Container>
            <ReaderPairingModal.AdvancedOptions.Button
              type="link"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            >
              {showAdvancedOptions ? 'Hide' : 'Show'} Advance Options
            </ReaderPairingModal.AdvancedOptions.Button>
            {showAdvancedOptions && (
              <ReaderPairingModal.AdvancedOptions.Error
                type="error"
                icon
                message={stripeTerminalStore.androidInitializationError?.message}
              />
            )}
          </ReaderPairingModal.AdvancedOptions.Container>
        )}
      </ReaderPairingModal.Container>
    </ReaderPairingModal.Modal>
  );
}

const ReaderPairingSteps = [
  'Make sure the reader is fully charged, indicated by a green light in the top-left corner of the screen.',
  'Turn the Reader off and on again.',
  'Swipe right from the left side of the screen and tap settings.',
  'Enter the admin pin code 07139.',
  'If needed, update your wifi settings to match the network that this Kiosk is on.',
  'Tap "generate pairing code".',
  'Enter the pairing code below.',
];

ReaderPairingModal.Modal = styled(Modal)`
  .ant-modal-body {
    padding: 1.5rem;
  }

  .ant-modal-content {
    border-radius: var(--border-radius__small);
  }
`;

ReaderPairingModal.Container = styled(Container)`
  margin-top: ${header.mobile.height}px;

  @media (min-width: ${breakpoints.md}px) {
    margin-bottom: 2rem;
    margin-top: 0;
    max-width: 60rem;
    padding: 1.5rem 2rem 0;
  }
`;

ReaderPairingModal.Header = styled.div`
  margin-bottom: 1rem;
`;

ReaderPairingModal.Title = styled.h3`
  margin-bottom: 5px;
  text-transform: uppercase;
`;

ReaderPairingModal.Content = styled.div``;

ReaderPairingModal.Description = styled.p`
  font-size: 0.875rem;
  margin-bottom: 0;
  margin-top: 0rem;
  padding-right: 2.5rem;
`;

ReaderPairingModal.InstructionsList = styled.ul`
  display: block;
  margin-bottom: 2rem;
  margin-left: 2rem;
  padding-inline-start: 0;
`;

ReaderPairingModal.InstructionsListItem = styled.li``;

ReaderPairingModal.FormContent = styled.div`
  display: block;
  width: 100%;

  #reader_registration_code {
    border-radius: var(--border-radius__regular) 0 0 var(--border-radius__regular);
  }
`;

ReaderPairingModal.AdvancedOptions = {
  Button: styled(Button)`
    color: var(--color-neutral__body);
    opacity: 0.8;
  `,
  Container: styled.div`
    padding: 1rem 0;
  `,
  Error: styled(Alert)`
    margin-top: 1rem;
  `,
};

export default observer(ReaderPairingModal);
